import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  X,
  Download,
  FileJson,
  FileText,
  Package,
  Clock,
  ClipboardCheck,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Link as LinkIcon,
} from 'lucide-react';
import {
  useSerializedUnit,
  exportAsBuiltJSON,
  type SerializedUnitDetail,
} from '../hooks/useSerialization';

interface AsBuiltViewerProps {
  serialNumber: string;
  onClose: () => void;
}

const statusConfig: Record<string, { label: string; color: string; icon: typeof CheckCircle }> = {
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  complete: { label: 'Complete', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  scrapped: { label: 'Scrapped', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const testResultConfig: Record<string, { label: string; color: string; icon: typeof CheckCircle }> = {
  pass: { label: 'Pass', color: 'text-success', icon: CheckCircle },
  fail: { label: 'Fail', color: 'text-danger', icon: XCircle },
  pending: { label: 'Pending', color: 'text-warning', icon: AlertCircle },
};

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

export function AsBuiltViewer({ serialNumber, onClose }: AsBuiltViewerProps) {
  const { data: unit, isLoading, error } = useSerializedUnit(serialNumber);
  const [activeTab, setActiveTab] = useState<'components' | 'labor' | 'tests'>('components');

  const handleExportJSON = () => {
    if (!unit) return;
    const json = exportAsBuiltJSON(unit);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `as-built-${unit.serialNumber}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const config = unit ? statusConfig[unit.status] : null;
  const StatusIcon = config?.icon ?? CheckCircle;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-background rounded-lg shadow-xl w-full max-w-4xl border border-border max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-foreground">
                As-Built Documentation
              </h2>
              {unit && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
                  <StatusIcon className={`h-3 w-3 ${unit.status === 'in_progress' ? 'animate-spin' : ''}`} />
                  {config?.label}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unit && (
                <button
                  onClick={handleExportJSON}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-foreground-muted hover:text-foreground border border-border rounded-md hover:bg-background-muted transition-colors"
                >
                  <FileJson className="h-4 w-4" />
                  Export JSON
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1 rounded hover:bg-background-muted transition-colors"
              >
                <X className="h-5 w-5 text-foreground-muted" />
              </button>
            </div>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="flex items-center gap-3 text-danger">
                <AlertCircle className="h-5 w-5" />
                <span>{error instanceof Error ? error.message : 'Failed to load'}</span>
              </div>
            </div>
          ) : unit ? (
            <div className="flex-1 overflow-y-auto">
              {/* Unit info */}
              <div className="p-4 bg-background-subtle border-b border-border">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-foreground-muted uppercase tracking-wider">Serial Number</p>
                    <p className="font-mono font-semibold text-foreground">{unit.serialNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-muted uppercase tracking-wider">Part Number</p>
                    <p className="font-medium text-foreground">{unit.partNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-muted uppercase tracking-wider">Sales Order</p>
                    <p className="font-medium text-foreground">{unit.gpSalesOrderNumber ?? '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-muted uppercase tracking-wider">Created</p>
                    <p className="font-medium text-foreground">
                      {format(parseISO(unit.createdAt), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                </div>
                {unit.customerName && (
                  <div className="mt-3">
                    <p className="text-xs text-foreground-muted uppercase tracking-wider">Customer</p>
                    <p className="font-medium text-foreground">{unit.customerName}</p>
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="border-b border-border">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('components')}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'components'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    <Package className="h-4 w-4" />
                    Components ({unit.components.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('labor')}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'labor'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    <Clock className="h-4 w-4" />
                    Labor ({unit.labor.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('tests')}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'tests'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    <ClipboardCheck className="h-4 w-4" />
                    Tests ({unit.tests.length})
                  </button>
                </div>
              </div>

              {/* Tab content */}
              <div className="p-4">
                {activeTab === 'components' && (
                  <ComponentsTab components={unit.components} />
                )}
                {activeTab === 'labor' && (
                  <LaborTab labor={unit.labor} />
                )}
                {activeTab === 'tests' && (
                  <TestsTab tests={unit.tests} />
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ComponentsTab({ components }: { components: SerializedUnitDetail['components'] }) {
  if (components.length === 0) {
    return (
      <div className="text-center py-8 text-foreground-muted">
        <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No components recorded</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-foreground-muted uppercase tracking-wider border-b border-border">
            <th className="pb-2 font-medium">Part Number</th>
            <th className="pb-2 font-medium">Quantity</th>
            <th className="pb-2 font-medium">Lot/Batch</th>
            <th className="pb-2 font-medium">Child Serial</th>
            <th className="pb-2 font-medium">Consumed</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {components.map((component) => (
            <tr key={component.id} className="hover:bg-background-subtle">
              <td className="py-2 font-mono font-medium text-foreground">{component.partNumber}</td>
              <td className="py-2 text-foreground">{component.quantity}</td>
              <td className="py-2 text-foreground-muted">{component.lotNumber ?? '-'}</td>
              <td className="py-2">
                {component.childSerialNumber ? (
                  <span className="inline-flex items-center gap-1 text-primary">
                    <LinkIcon className="h-3 w-3" />
                    {component.childSerialNumber}
                  </span>
                ) : (
                  <span className="text-foreground-muted">-</span>
                )}
              </td>
              <td className="py-2 text-foreground-muted">
                {format(parseISO(component.consumedAt), 'MMM d, h:mm a')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LaborTab({ labor }: { labor: SerializedUnitDetail['labor'] }) {
  if (labor.length === 0) {
    return (
      <div className="text-center py-8 text-foreground-muted">
        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No labor recorded</p>
      </div>
    );
  }

  const totalMinutes = labor.reduce((sum, l) => sum + l.durationMinutes, 0);

  return (
    <div>
      <div className="mb-4 p-3 bg-background-subtle rounded-md">
        <p className="text-sm text-foreground-muted">
          Total Labor: <span className="font-semibold text-foreground">{formatDuration(totalMinutes)}</span>
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-foreground-muted uppercase tracking-wider border-b border-border">
              <th className="pb-2 font-medium">Worker</th>
              <th className="pb-2 font-medium">Labor Type</th>
              <th className="pb-2 font-medium">Duration</th>
              <th className="pb-2 font-medium">Start</th>
              <th className="pb-2 font-medium">End</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {labor.map((entry) => (
              <tr key={entry.id} className="hover:bg-background-subtle">
                <td className="py-2 font-medium text-foreground">{entry.personName}</td>
                <td className="py-2 text-foreground">{entry.laborBucket}</td>
                <td className="py-2 text-foreground">{formatDuration(entry.durationMinutes)}</td>
                <td className="py-2 text-foreground-muted">
                  {format(parseISO(entry.startTime), 'MMM d, h:mm a')}
                </td>
                <td className="py-2 text-foreground-muted">
                  {format(parseISO(entry.endTime), 'MMM d, h:mm a')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TestsTab({ tests }: { tests: SerializedUnitDetail['tests'] }) {
  if (tests.length === 0) {
    return (
      <div className="text-center py-8 text-foreground-muted">
        <ClipboardCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No tests recorded</p>
      </div>
    );
  }

  const defaultConfig = { label: 'Unknown', color: 'text-foreground-muted', icon: AlertCircle };

  return (
    <div className="space-y-3">
      {tests.map((test) => {
        const config = testResultConfig[test.result] ?? defaultConfig;
        const Icon = config.icon;

        return (
          <div
            key={test.id}
            className="flex items-start gap-3 p-3 bg-background-subtle rounded-md border border-border"
          >
            <Icon className={`h-5 w-5 mt-0.5 ${config.color}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{test.testName}</span>
                <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
              </div>
              <p className="text-sm text-foreground-muted mt-1">
                Tested by {test.testerName ?? test.testerId} on{' '}
                {format(parseISO(test.testedAt), 'MMM d, yyyy h:mm a')}
              </p>
              {test.notes && (
                <p className="text-sm text-foreground mt-2">{test.notes}</p>
              )}
              {test.attachmentR2Key && (
                <p className="text-sm text-primary mt-2">
                  <a href="#" className="hover:underline">View Attachment</a>
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
